/**
 * El paso 1: soltar el archivo.
 *
 * Un solo cuadro y nada mas. El proveedor no se pregunta aca: se lee del PDF,
 * y si no esta cargado el wizard ofrece crearlo en el paso siguiente. Pedirlo
 * antes de leer el archivo es pedirle al operador que sepa lo que el sistema
 * esta por averiguar.
 */
import { useRef, useState, type DragEvent } from "react";
import { FileUp, Paperclip, X } from "lucide-react";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { Progreso } from "@/components/ui/progreso";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

import { EXTENSIONES_ACEPTADAS, formatearBytes, validarArchivo } from "./logica";

export interface SubidaRemitoProps {
  subiendo: boolean;
  /** Porcentaje de bytes enviados. Solo se muestra mientras `subiendo`. */
  progreso: number;
  onSubir: (archivo: File) => void;
}

export function SubidaRemito({ subiendo, progreso, onSubir }: SubidaRemitoProps): JSX.Element {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const [encima, setEncima] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function tomar(elegido: File | null | undefined): void {
    if (!elegido) return;
    const validacion = validarArchivo(elegido);
    if (!validacion.valida) {
      setArchivo(null);
      setProblema(validacion.motivo ?? "No podemos leer ese archivo.");
      return;
    }
    setProblema(null);
    setArchivo(elegido);
  }

  function soltar(evento: DragEvent<HTMLElement>): void {
    evento.preventDefault();
    setEncima(false);
    tomar(evento.dataTransfer.files.item(0));
  }

  return (
    // Columna flex: la pagina la estira hasta abajo (Pagina alto="fijo") y la
    // zona de soltar se lleva el alto que sobra. Arriba quedaba un tercio de
    // pantalla vacio y el blanco donde soltar era chico.
    <Card padding="normal" className="flex flex-col gap-4">
      <TituloSeccion descripcion="El sistema lee el proveedor, las líneas y las cantidades, y después te pregunta solo lo que no pudo resolver.">
        Soltá el remito o la factura
      </TituloSeccion>

      {/* La zona de soltar es una tarjeta punteada: "aca falta algo". */}
      <Card
        variant="dashed"
        padding="none"
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={soltar}
        className={cn(
          "flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center transition-colors duration-150",
          encima && "border-ring bg-accent",
        )}
      >
        <FileUp className="size-6 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-medium">Arrastrá el PDF o la foto acá</p>
          <p className="text-xs text-muted-foreground">
            PDF, imagen, Excel o texto. Hasta {formatearBytes(24 * 1024 * 1024)}.
          </p>
        </div>
        {/* <input type="file"> crudo: no hay primitivo para elegir archivos y el
            control nativo queda oculto; lo dispara el boton de abajo. */}
        <input
          ref={input}
          id="archivo-remito"
          type="file"
          className="sr-only"
          accept={EXTENSIONES_ACEPTADAS.join(",")}
          disabled={subiendo}
          onChange={(e) => tomar(e.target.files?.item(0))}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={subiendo}
          onClick={() => input.current?.click()}
        >
          Buscar en la computadora
        </Button>
      </Card>

      {problema ? (
        <Aviso tono="destructive" compacto>
          {problema}
        </Aviso>
      ) : null}

      {archivo ? (
        <Card
          variant="muted"
          padding="none"
          className="flex items-center justify-between gap-3 px-3 py-2"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Paperclip className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{archivo.name}</span>
              <span className="block text-xs tabular-nums text-muted-foreground">
                {formatearBytes(archivo.size)}
              </span>
            </span>
          </span>
          {!subiendo ? (
            <IconButton
              icono={X}
              aria-label="Sacar el archivo"
              tamano="sm"
              onClick={() => setArchivo(null)}
            />
          ) : null}
        </Card>
      ) : null}

      {subiendo ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">Subiendo</span>
            <span className="tabular-nums">{progreso} %</span>
          </div>
          <Progreso valor={progreso} aria-label="Subida del archivo" />
        </div>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Subir no suma nada al stock. Vas a revisar producto por producto antes de aplicar.
        </p>
        <Button
          type="button"
          onClick={() => archivo && onSubir(archivo)}
          disabled={archivo === null}
          cargando={subiendo}
        >
          Leer el remito
        </Button>
      </div>
    </Card>
  );
}
